import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Users, Loader2 } from "lucide-react";

const ROLES = ["admin", "installation_ops", "delivery_ops", "driver", "am"];
const TEAMS = ["installation", "delivery"];

const empty = { email: "", username: "", name: "", role: "driver", team: "installation", password: "" };

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const openNew = () => { setEditId(null); setF(empty); setOpen(true); };
  const openEdit = (u) => { setEditId(u.id); setF({ ...empty, name: u.name, role: u.role, team: u.team || "installation", email: u.email, username: u.username }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      if (editId) {
        const body = { name: f.name, role: f.role, team: f.role === "driver" ? f.team : null };
        if (f.password) body.password = f.password;
        await api.patch(`/users/${editId}`, body);
        toast.success("User updated");
      } else {
        if (!f.email || !f.name || !f.password) { setBusy(false); return toast.error("Email, name & password required"); }
        await api.post("/users", {
          email: f.email, username: f.username || undefined, name: f.name,
          role: f.role, password: f.password, team: f.role === "driver" ? f.team : undefined,
        });
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stark-ink sm:text-4xl">Team</h1>
          <p className="mt-1 text-sm text-stark-muted">Manage ops, account managers & field crew.</p>
        </div>
        <Button onClick={openNew} className="h-11 rounded-xl bg-stark-terracotta font-bold hover:bg-[#bd4f2c]" data-testid="add-user-btn">
          <UserPlus className="mr-1.5 h-4 w-4" /> Add member
        </Button>
      </div>

      <div className="mt-6 space-y-3">
        {!users ? (
          [...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 rounded-2xl" />)
        ) : users.length === 0 ? (
          <div className="stark-card py-16 text-center text-stark-muted"><Users className="mx-auto h-10 w-10" /><p className="mt-2">No users</p></div>
        ) : (
          users.map((u) => (
            <div key={u.id} className="stark-card flex items-center gap-4 p-4" data-testid={`user-row-${u.username}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-stark-moss text-sm font-bold text-white">
                {u.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-stark-ink">{u.name}</div>
                <div className="text-xs text-stark-muted">@{u.username} · {u.email}</div>
              </div>
              <div className="hidden text-right sm:block">
                <div className="text-sm font-semibold text-stark-ink">{ROLE_LABELS[u.role]}</div>
                {u.team && <div className="text-xs capitalize text-stark-muted">{u.team}</div>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`}><Pencil className="h-4 w-4" /></Button>
              {u.id !== me.id && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="text-stark-error"><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                      <AlertDialogDescription>They will lose access immediately.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(u.id)} className="bg-stark-error hover:bg-stark-error/90">Remove</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          ))
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit member" : "Add member"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Full name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="user-name-input" /></div>
            {!editId && (
              <>
                <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="user-email-input" /></div>
                <div><Label>Username <span className="font-normal text-stark-muted">(optional)</span></Label><Input value={f.username} onChange={(e) => set("username", e.target.value)} className="mt-1.5" /></div>
              </>
            )}
            <div>
              <Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {f.role === "driver" && (
              <div>
                <Label>Team</Label>
                <Select value={f.team} onValueChange={(v) => set("team", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{TEAMS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>{editId ? "New password (optional)" : "Password"}</Label><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" data-testid="user-password-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-stark-terracotta hover:bg-[#bd4f2c]" disabled={busy} onClick={save} data-testid="user-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
